import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/hooks/useTheme';
import type { MapMouseEvent } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, Map as Mapbox, Popup, Source, type MapRef } from 'react-map-gl/mapbox';
import { useNavigate } from 'react-router';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWN0ZWdvbiIsImEiOiJjbWJ3MW8ycW0wdW04MmpzMDE4d3g2YXh4In0.iV9tT4QGO3U112GMEyn2Cg';

interface DashboardProject {
  id: string;
  name: string;
  positionWGS84?: { type: string; coordinates: number[] } | null;
  storageUsedBytes: number;
}

interface ProjectsMapProps {
  projects: DashboardProject[];
}

const ProjectsMap: FunctionComponent<ProjectsMapProps> = ({ projects }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const mapRef = useRef<MapRef>(null);
  const [hoverInfo, setHoverInfo] = useState<{ lng: number; lat: number; name: string } | null>(null);

  const projectsWithCoords = useMemo(() => {
    return projects.filter((p) => {
      if (!p.positionWGS84) return false;
      const coords = p.positionWGS84.coordinates;
      if (!coords || coords.length < 2) return false;
      const [lng, lat] = coords;
      return lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
    });
  }, [projects]);

  const geojson = useMemo(() => {
    return {
      type: 'FeatureCollection' as const,
      features: projectsWithCoords.map((p) => ({
        type: 'Feature' as const,
        properties: { id: p.id, name: p.name },
        geometry: {
          type: 'Point' as const,
          coordinates: [p.positionWGS84!.coordinates[0], p.positionWGS84!.coordinates[1]],
        },
      })),
    };
  }, [projectsWithCoords]);

  const initialView = useMemo(() => {
    return { longitude: 10, latitude: 48, zoom: 2 };
  }, []);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || projectsWithCoords.length === 0) return;

    if (projectsWithCoords.length === 1) {
      const [lng, lat] = projectsWithCoords[0].positionWGS84!.coordinates;
      map.flyTo({ center: [lng, lat], zoom: 10, padding: { top: 60, bottom: 60, left: 60, right: 60 } });
      return;
    }

    let minLng = 180,
      maxLng = -180,
      minLat = 90,
      maxLat = -90;
    projectsWithCoords.forEach((p) => {
      const [lng, lat] = p.positionWGS84!.coordinates;
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: { top: 60, bottom: 60, left: 60, right: 60 }, maxZoom: 12, duration: 0 },
    );
  }, [projectsWithCoords]);

  const handleClick = useCallback(
    (e: MapMouseEvent) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      // Check clusters first
      const clusterFeatures = map.queryRenderedFeatures(e.point, { layers: ['project-clusters'] });
      if (clusterFeatures.length > 0) {
        const feature = clusterFeatures[0];
        const clusterId = feature.properties?.cluster_id;
        const source = map.getSource('projects') as mapboxgl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
          if (err || !feature.geometry || feature.geometry.type !== 'Point') return;
          map.easeTo({
            center: feature.geometry.coordinates as [number, number],
            zoom: zoom + 1,
          });
        });
        return;
      }

      // Check individual points
      const pointFeatures = map.queryRenderedFeatures(e.point, { layers: ['project-points'] });
      if (pointFeatures.length > 0) {
        const projectId = pointFeatures[0].properties?.id;
        if (projectId) navigate(`/projects/${projectId}`);
      }
    },
    [navigate],
  );

  const handleMouseMove = useCallback((e: MapMouseEvent) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const features = map.queryRenderedFeatures(e.point, { layers: ['project-points'] });
    if (features.length > 0) {
      map.getCanvas().style.cursor = 'pointer';
      const f = features[0];
      if (f.geometry.type === 'Point') {
        setHoverInfo({
          lng: f.geometry.coordinates[0],
          lat: f.geometry.coordinates[1],
          name: f.properties?.name || '',
        });
      }
    } else {
      // Check clusters for pointer cursor
      const clusterFeatures = map.queryRenderedFeatures(e.point, { layers: ['project-clusters'] });
      map.getCanvas().style.cursor = clusterFeatures.length > 0 ? 'pointer' : '';
      setHoverInfo(null);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) map.getCanvas().style.cursor = '';
    setHoverInfo(null);
  }, []);

  const popupAnchor = useMemo(() => {
    if (!hoverInfo) return 'bottom' as const;
    const map = mapRef.current?.getMap();
    if (!map) return 'bottom' as const;

    const px = map.project([hoverInfo.lng, hoverInfo.lat]);
    const canvas = map.getCanvas();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    const nearTop = px.y < 50;
    const nearLeft = px.x < 100;
    const nearRight = px.x > w - 100;

    if (nearTop && nearLeft) return 'top-left' as const;
    if (nearTop && nearRight) return 'top-right' as const;
    if (nearTop) return 'top' as const;
    if (nearLeft) return 'left' as const;
    if (nearRight) return 'right' as const;
    return 'bottom' as const;
  }, [hoverInfo]);

  const mapStyle = theme === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11';
  const isDark = theme === 'dark';

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-sm'>{t('dashboard.map.title')}</CardTitle>
        <CardDescription>{t('dashboard.map.description', { count: projectsWithCoords.length })}</CardDescription>
      </CardHeader>
      <CardContent className='!p-0'>
        <div className='h-[380px] relative overflow-hidden rounded-b-lg'>
          {projectsWithCoords.length > 0 ? (
            <Mapbox
              ref={mapRef}
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={initialView}
              style={{ width: '100%', height: '100%' }}
              mapStyle={mapStyle}
              attributionControl={false}
              onLoad={handleMapLoad}
              onClick={handleClick}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              interactiveLayerIds={['project-clusters', 'project-points']}
              reuseMaps>
              <Source id='projects' type='geojson' data={geojson} cluster clusterMaxZoom={14} clusterRadius={50}>
                {/* Cluster circles */}
                <Layer
                  id='project-clusters'
                  type='circle'
                  filter={['has', 'point_count']}
                  paint={{
                    'circle-color': isDark ? '#818cf8' : '#6366f1',
                    'circle-radius': ['step', ['get', 'point_count'], 18, 5, 22, 10, 26, 25, 30],
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                  }}
                />
                {/* Cluster count labels */}
                <Layer
                  id='project-cluster-count'
                  type='symbol'
                  filter={['has', 'point_count']}
                  layout={{
                    'text-field': '{point_count_abbreviated}',
                    'text-size': 12,
                    'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
                  }}
                  paint={{
                    'text-color': '#ffffff',
                  }}
                />
                {/* Individual project dots */}
                <Layer
                  id='project-points'
                  type='circle'
                  filter={['!', ['has', 'point_count']]}
                  paint={{
                    'circle-color': isDark ? '#818cf8' : '#6366f1',
                    'circle-radius': 6,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                  }}
                />
              </Source>
              {hoverInfo && (
                <Popup
                  longitude={hoverInfo.lng}
                  latitude={hoverInfo.lat}
                  anchor={popupAnchor}
                  offset={12}
                  closeButton={false}
                  closeOnClick={false}
                  className='[&_.mapboxgl-popup-content]:!bg-slate-800 [&_.mapboxgl-popup-content]:!text-white [&_.mapboxgl-popup-content]:!text-xs [&_.mapboxgl-popup-content]:!font-medium [&_.mapboxgl-popup-content]:!px-3 [&_.mapboxgl-popup-content]:!py-1.5 [&_.mapboxgl-popup-content]:!rounded-md [&_.mapboxgl-popup-content]:!shadow-lg [&_.mapboxgl-popup-tip]:!border-t-slate-800 [&_.mapboxgl-popup-tip]:!border-b-slate-800'>
                  {hoverInfo.name}
                </Popup>
              )}
            </Mapbox>
          ) : (
            <div className='h-full flex items-center justify-center bg-slate-50 dark:bg-stone-900'>
              <div className='text-center'>
                <svg viewBox='0 0 24 24' className='size-12 mx-auto mb-3 fill-slate-300 dark:fill-stone-600'>
                  <path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' />
                </svg>
                <p className='text-sm text-slate-400 dark:text-stone-500 font-medium'>
                  {t('dashboard.map.noProjects')}
                </p>
                <p className='text-xs text-slate-400/70 dark:text-stone-500/70 mt-1'>
                  {t('dashboard.map.noProjectsHint')}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectsMap;
